import {
  useProgrammeStore,
  useNavigationStore,
  useDocumentsStore,
  useLanguageStore,
  useUserStore,
  useProfileStore,
  useJobmatchStore
} from '@/stores'
import { factory } from '../../../../factory-api/factory'
import { defineStore } from 'pinia'
import router from '@/router'
import { useAppStore } from './app'
import { UserType, type AuthProgram, type ChangePasswordRequest } from '../../../../generated-api'
import { getLocaleBasedOnDeviceLocale } from '@/application/helpers/languageHelpers'
import { Capacitor } from '@capacitor/core'
import { ref } from 'vue'

export const useAuthStore = defineStore('auth', () => {
  const appStore = useAppStore()
  const navigationStore = useNavigationStore()
  const languageStore = useLanguageStore()
  const programmeStore = useProgrammeStore()
  const documentStore = useDocumentsStore()
  const userStore = useUserStore()
  const profileStore = useProfileStore()
  const jobMatchStore = useJobmatchStore()

  const isAuthenticated = ref(false)
  const showTechincalError = ref(false)
  const currentlyAuthenticating = ref(false)
  const loginFailed = ref(false)

  const route = useRoute()

  // Actions: Used for asynchronous operations and side effects.
  async function login(username: string, password: string) {
    setCurrentlyAuthenticating(true)
    try {
      const api = await factory.getAuthorizationApi()
      const resultAsJson = await api.login({
        authenticationRequest: {
          username: username.toLowerCase(),
          password: password
        }
      })

      if (
        resultAsJson !== undefined &&
        resultAsJson.member !== undefined &&
        resultAsJson.authenticated !== undefined
      ) {
        setIsAuthenticated(resultAsJson.authenticated)
        userStore.setMember(resultAsJson.member)

        await filterProgrammes(resultAsJson.member?.programmes, resultAsJson.member?.program)

        setLoginFailed(false)

        if (Capacitor.isNativePlatform()) {
          const language =
            userStore.member?.language !== undefined ? userStore.member.language : 'English'
          // If current url is not the same as appLanguageUrl, login with token to new backend.
          console.log('LANGUAGE CHANGER: login native', language)

          const memberLocale = getLocaleBasedOnDeviceLocale(language)

          const appLanguageLocale =
            localStorage.getItem('appLanguageLocale') != null
              ? localStorage.getItem('appLanguageLocale')?.toString()
              : ''

          const deviceLocale = getLocaleBasedOnDeviceLocale(appLanguageLocale)

          if (memberLocale != deviceLocale) {
            console.log('languageBasedLogin')
            await languageBasedLogin(language, false)
          }
        }

        await appStore.getSiteContent()
        return true
      } else {
        setIsAuthenticated(false)
        setCurrentlyAuthenticating(false)
        setLoginFailed(true)
        return false
      }
    } catch (error) {
      setIsAuthenticated(false)
      setCurrentlyAuthenticating(false)
      setLoginFailed(true)
      return false
    }
  }

  const filterProgrammes = async (
    programmes: AuthProgram[] | undefined,
    selectedProgramme: AuthProgram | undefined
  ) => {
    let filteredProgrammes: AuthProgram[] = []

    //Get all programmes from backend, remove all programs that is not on current domain
    //If its native, remove all programs that have useNewFrontpage set to false
    if (programmes !== undefined) {
      //Removing all program that are not on same domain, means all A2B programmes get removed if domain is as3, and if the app is A2B, we remove BTB, and Work&Care programs
      const programmesFromDomain = programmes.filter((p) => p.isOnCurrentDomain === true)

      if (Capacitor.isNativePlatform()) {
        //On native we are removing all program that is not eligible to use the app. Which means useNewFrontpage is false
        filteredProgrammes = programmesFromDomain.filter((p) => p.useNewFrontpage === true)
      } else if (Capacitor.isNativePlatform() === false) {
        filteredProgrammes = programmesFromDomain
      }

      if (filteredProgrammes.length === 0 && Capacitor.isNativePlatform()) {
        //If all programs is legacy, length of filteredProgrammesis 0, and there we show validation message, but only for native
        appStore.setShowValidationPage(true)
      } else if (filteredProgrammes.length === 0 && Capacitor.isNativePlatform() === false) {
        appStore.setShowValidationPage(true)
      } else {
        setProgrammes(filteredProgrammes)

        if (selectedProgramme !== undefined && selectedProgramme.consentDate !== undefined) {
          //Selected programme is used on refresh, because on refresh the member already have programm selected. And therefore there is no need to run programmeStore.selectProgramme
          programmeStore.setSelectedProgramme(selectedProgramme)
        } else if (
          filteredProgrammes.length === 1 &&
          filteredProgrammes[0].consentDate !== undefined
        ) {
          //If user has one program after filter, and terms is accepted, we auto select programme for the user, and they will be redirected to the frontpage
          await programmeStore.selectProgramme(filteredProgrammes[0])
        }
      }
    }
  }

  async function languageBasedLogin(language: string, isSelectedLanguage: boolean) {
    if (userStore.member !== undefined && userStore.member.userName !== undefined) {
      appStore.setAppSpinner(true)

      let api = await factory.getAuthorizationApi()

      try {
        // Get token to login to other page
        const tokenResponse = await api.getLoginToken()
        // Get username from member to use in login with token
        const username = userStore.member.userName.toLowerCase()
        // Save the program to use later for login with token to set the program
        const program = programmeStore.selectedProgramme

        navigationStore.setCurrentNavigationUrl('')

        // These temps are only to be used on web, since choosen a language will overwrite these values, when we run the method setApiBasedOnSelectedLanguage. When we have logged in on the new backend, we need to reset these values to their old values, so the chosen language is not stored as the backend
        const tempUrl = localStorage.getItem('appLanguageUrl') ?? ''
        const tempLocale = localStorage.getItem('appLanguageLocale') ?? ''
        const tempAppUrl = localStorage.getItem('webAppUrl') ?? ''

        // Set the apiUrl to the selected language, else its from backend
        console.log('languageBasedLogin', language)
        if (isSelectedLanguage === true) {
          await languageStore.setApiBasedOnSelectedLanguage(language)
        } else {
          languageStore.setLanguageBasedOnFocus(language)
        }

        // Set the api again, since the backend have changed
        api = await factory.getAuthorizationApi()

        const resultAsJson = await api.login({
          authenticationRequest: {
            username: username,
            token: tokenResponse
          }
        })

        if (resultAsJson !== undefined && resultAsJson.authenticated !== undefined) {
          setIsAuthenticated(resultAsJson.authenticated)

          if (program != undefined) {
            await programmeStore.selectProgramme(program)
          }
        }

        if (Capacitor.isNativePlatform() === false) {
          // use the temp values to reset localstorageitems to their original value
          languageStore.setLocalStorageItems(tempUrl, tempLocale, tempAppUrl)

          const webUrl = languageStore.getWebAppUrl(language) ?? ''
          const isLocalEnvironment = import.meta.env.VITE_ENVIRONMENT === 'local'
          const protocol = isLocalEnvironment ? 'http' : 'https'
          const port = isLocalEnvironment ? ':5173' : ''
          const redirectUrl = `${protocol}://${webUrl}${port}`

          await window.location.replace(redirectUrl)

          return true
        }

        setCurrentlyAuthenticating(false)

        //When we are on / we do not hit the router, like the content pages, and therefore content do not update
        //So we need to tell the system to get new content on /
        if (route.path === '/') {
          appStore.getContent('/')
        }

        return true
      } catch (err) {
        console.log('login err ', err)
        setIsAuthenticated(false)
        setCurrentlyAuthenticating(false)
        setLoginFailed(true)
        appStore.setAppSpinner(false)
        return false
      }
    }

    return false
  }

  function setProgrammes(programmes: AuthProgram[] | undefined) {
    const listOfProgrammes: AuthProgram[] = []

    if (programmes !== undefined) {
      programmes.forEach((programme) => {
        listOfProgrammes.push(programme)
      })
    }

    programmeStore.setProgrammes(listOfProgrammes)
  }

  async function authenticate() {
    try {
      const api = await factory.getAuthorizationApi()
      console.log('BEFORE ISAUTHENTICATED')
      const response = await api.isAuthenticated()

      if (response !== undefined && response.authenticated !== undefined) {
        setIsAuthenticated(response.authenticated)
        setShowTechincalError(response.technicalIssueError ?? false)
      }

      if (response.member != undefined) {
        await filterProgrammes(response.member.programmes, response.member.program)
        userStore.setMember(response.member)
      }
    } catch (error) {
      setIsAuthenticated(false)
    }
  }

  async function changePassword(passwordRequest: ChangePasswordRequest) {
    try {
      const api = await factory.getAuthorizationApi()
      const result = await api.changePassword({
        changePasswordRequest: {
          newPassword: passwordRequest.newPassword,
          oldPassword: passwordRequest.oldPassword
        }
      })
      return result
    } catch (error) {
      console.log(error)
      return error
    }
  }

  async function requestResetPassword(userEmail: string) {
    try {
      const api = await factory.getAuthorizationApi()
      const response = await api.requestResetPassword({
        resetPasswordRequest: { email: userEmail, userType: UserType.Candidate }
      })
      return response
    } catch (error) {
      console.log(error)
      return
    }
  }

  async function executeResetPassword(userEmail: string, newPassword: string, pincode: string) {
    try {
      const api = await factory.getAuthorizationApi()
      const response = await api.executeResetPassword({
        resetPasswordParameter: {
          email: userEmail,
          password: newPassword,
          pin: pincode,
          userType: UserType.Candidate
        }
      })
      return response
    } catch (error) {
      console.log(error)
      return
    }
  }

  async function validatePincode(pincode: string, userEmail: string) {
    try {
      const api = await factory.getAuthorizationApi()
      const response = await api.validatePincode({
        pincode: pincode,
        userId: userEmail,
        userType: UserType.Candidate
      })
      return response
    } catch (error) {
      console.log(error)
      return
    }
  }

  async function changeUsername(username: string | undefined) {
    if (username !== undefined) {
      try {
        const api = await factory.getAuthorizationApi()
        return await api.changeUsername({ username: username })
      } catch (error) {
        console.log(error)
        return false
      }
    } else {
      console.warn('Missing username')
      return false
    }
  }

  async function signOut() {
    const api = await factory.getAuthorizationApi()
    try {
      console.log('SIGN USER OUT - RESET EVERYTHING')

      const response = await api.logout()

      if (response.authenticated === false) {
        // push router to login at top,
        // helps errors happinging when clearing stores on specific pages
        await router.push('/login')

        console.log('SIGN OUT: AUTHENTICATED IS FALSE')

        userStore.clearMember()

        appStore.resetAppStore()

        // RESET PROGRAMME STORE
        programmeStore.resetProgrammeStore()

        // RESET NAVIGATION STORE
        navigationStore.resetNavigationStore()

        languageStore.resetLanguageStore()

        documentStore.resetDocumentStore()

        profileStore.resetProfileStores()

        jobMatchStore.resetJobmatchStore()

        console.log('signout ran to end')
      }
    } catch (error) {
      console.error(error)
    }
  }

  function setIsAuthenticated(bool: boolean) {
    isAuthenticated.value = bool
  }

  function setCurrentlyAuthenticating(authenticating: boolean) {
    currentlyAuthenticating.value = authenticating
  }

  function cancelLoginFlow() {
    signOut()
  }

  function setLoginFailed(bool: boolean) {
    loginFailed.value = bool
  }

  const setShowTechincalError = (visible: boolean) => {
    showTechincalError.value = visible
  }

  return {
    isAuthenticated,
    currentlyAuthenticating,
    loginFailed,
    login,
    authenticate,
    changePassword,
    changeUsername,
    signOut,
    setCurrentlyAuthenticating,
    requestResetPassword,
    executeResetPassword,
    languageBasedLogin,
    validatePincode,
    cancelLoginFlow,
    setLoginFailed,
    setShowTechincalError,
    showTechincalError
  }
})
